import type { ElementType, HTMLAttributes, ReactNode } from "react";
import styles from "./Text.module.css";
import { cx } from "./cx.js";

type Size = "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl";
type Weight = "normal" | "medium" | "semibold";
type Tone = "primary" | "secondary" | "muted" | "positive" | "warning" | "danger";

interface TextProps extends HTMLAttributes<HTMLElement> {
  size?: Size;
  weight?: Weight;
  tone?: Tone;
  truncate?: boolean;
  as?: ElementType;
  children?: ReactNode;
}

export function Text({
  size = "base",
  weight = "normal",
  tone = "primary",
  truncate,
  as: Tag = "p",
  className,
  children,
  ...rest
}: TextProps) {
  return (
    <Tag
      className={cx(
        styles.text,
        styles[`size-${size}`],
        styles[`weight-${weight}`],
        styles[`tone-${tone}`],
        truncate && styles.truncate,
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

const HEADING_SIZE: Record<1 | 2 | 3 | 4, Size> = { 1: "3xl", 2: "2xl", 3: "xl", 4: "lg" };

interface HeadingProps extends Omit<TextProps, "as"> {
  /** The semantic level. Size follows from it unless overridden. */
  level?: 1 | 2 | 3 | 4;
}

/**
 * Level drives the tag, size defaults to match, and the two can be decoupled —
 * so a visually small heading does not have to break the document outline.
 */
export function Heading({
  level = 2,
  size,
  weight = "semibold",
  className,
  ...rest
}: HeadingProps) {
  return (
    <Text
      as={`h${level}`}
      size={size ?? HEADING_SIZE[level]}
      weight={weight}
      className={cx(styles.heading, className)}
      {...rest}
    />
  );
}
